import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, Typography, Space, Spin, Tag, Button, Tabs } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { bulkUpdateAppointmentsByProposalAndAccount, getInspectionPlanByProposal } from '../../inspection-appointments/services/inspection-appointmentService';
import AttributeService from '../../../services/AttributeService';
import ToolService from '../../../services/ToolService';
import { listReportFormatAttributeStatuses } from '../../report-formats/services/report-formatService';
import type { ReportFormatAttributeStatus } from '../../report-formats/types/ReportFormat';
import { listInspectorsAll } from '../services/inspectorService';
import InspectorControlAddressCard from 'components/InspectorControlAddressCard';
import InspectorPlanAuthorizedContactsCard from 'components/InspectorPlanAuthorizedContactsCard';
import InspectorPlanTasksCard, { ReportFormatStatus } from 'components/InspectorPlanTasksCard';
import { TRANSPORTATION_TYPE_LABELS, TransportationType, hasRole, openNotification } from '../../../utils';
import DocumentService from '../../../services/DocumentService';
import type { UploadFile } from 'antd/es/upload/interface';
import IsgKatipContractDrawer from '../components/IsgKatipContractDrawer';

const { Text } = Typography;
const { TabPane } = Tabs;

dayjs.locale('tr');

const PLAN_DETAIL_TAB_KEYS = ['control-address', 'plan-authorized', 'transportation', 'tasks'] as const;
type PlanDetailTabKey = typeof PLAN_DETAIL_TAB_KEYS[number];
const DEFAULT_PLAN_DETAIL_TAB: PlanDetailTabKey = 'control-address';

const resolvePlanDetailTab = (search: string): PlanDetailTabKey => {
  const tab = new URLSearchParams(search).get('tab');
  return PLAN_DETAIL_TAB_KEYS.includes(tab as PlanDetailTabKey)
    ? (tab as PlanDetailTabKey)
    : DEFAULT_PLAN_DETAIL_TAB;
};

const DEFAULT_POSITION: [number, number] = [41.0082, 28.9784];

interface TransportationEntry {
  key: string;
  inspectorName: string;
  transport?: string;
  transportLabel?: string;
  note?: string;
  timeRange: string;
  fullTimeRange: string;
  dayKey: string;
  dayLabel: string;
  dayOfWeek: string;
  startTimestamp: number;
  location?: string;
  latitude?: number;
  longitude?: number;
}

interface TransportationGroup {
  dayKey: string;
  dayLabel: string;
  dayOfWeek: string;
  sortValue: number;
  entries: TransportationEntry[];
}

const resolveLocationLabel = (appointment: any, fallbackProposal: any): string | undefined => {
  const addressCandidates = [appointment?.address, fallbackProposal?.address];

  const pick = (source: any, keys: string[]) => {
    if (!source || typeof source !== 'object') {
      return undefined;
    }
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim().length) {
        return value.trim();
      }
    }
    return undefined;
  };

  for (const candidate of addressCandidates) {
    if (!candidate) continue;
    const district = pick(candidate, ['district', 'districtName', 'county', 'countyName', 'ilce', 'town']);
    const city = pick(candidate, ['city', 'cityName', 'province', 'provinceName', 'state', 'il']);
    const parts = [district, city].filter(Boolean);
    if (parts.length) {
      return parts.join(' / ');
    }
  }
  return undefined;
};

const resolveTransportationLabel = (value?: string | null): string | undefined => {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }
  const normalized = value.toUpperCase() as TransportationType;
  const labels = TRANSPORTATION_TYPE_LABELS as Record<string, string>;
  return labels[normalized] ?? value;
};

const toNumericId = (value: unknown): number | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }
  return undefined;
};

const resolvePlanAccountId = (source: any): number | undefined =>
  toNumericId(
    source?.accountId ??
      source?.account?.id ??
      source?.inspectorAccountId ??
      source?.inspectorId ??
      source?.inspector?.accountId ??
      source?.inspector?.account?.id ??
      source?.inspector?.id,
  );

const defaultMapIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const InspectorPlanDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [planData, setPlanData] = useState<any | null>(null);
  const [proposal, setProposal] = useState<any | null>(null);
  const [attributes, setAttributes] = useState<any[]>([]);
  const [tools, setTools] = useState<any[]>([]);
  const [reportFormatStatuses, setReportFormatStatuses] = useState<ReportFormatAttributeStatus[]>([]);
  const [inspectors, setInspectors] = useState<any[]>([]);
  const [contractFileList, setContractFileList] = useState<UploadFile<any>[]>([]);
  const [contractIdDraft, setContractIdDraft] = useState<string>('');
  const [contractIdSaving, setContractIdSaving] = useState<boolean>(false);
  const [contractDrawerOpen, setContractDrawerOpen] = useState<boolean>(false);
  const [activeTabKey, setActiveTabKey] = useState<PlanDetailTabKey>(() => resolvePlanDetailTab(location.search));
  const [planReloadVersion, setPlanReloadVersion] = useState(0);
  const isAdmin = useMemo(() => hasRole('ADMIN'), []);
  const isCentralUser = useMemo(() => hasRole('CENTRAL_USER'), []);
  const isInspector = useMemo(() => hasRole('INSPECTOR'), []);

  useEffect(() => {
    setActiveTabKey(resolvePlanDetailTab(location.search));
  }, [location.search]);

  const handleTabChange = (key: string) => {
    const tabKey = PLAN_DETAIL_TAB_KEYS.includes(key as PlanDetailTabKey)
      ? (key as PlanDetailTabKey)
      : DEFAULT_PLAN_DETAIL_TAB;
    setActiveTabKey(tabKey);

    const searchParams = new URLSearchParams(location.search);
    if (tabKey === DEFAULT_PLAN_DETAIL_TAB) {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tabKey);
    }
    const nextSearch = searchParams.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
    navigate(nextUrl, { replace: false });
  };

  useEffect(() => {
    let ignore = false;
    const proposalId = Number(id);
    if (Number.isNaN(proposalId) || proposalId <= 0) {
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setProposal(null);
        const [planRes, attributeRes, toolRes, reportFormatStatusRes, inspectorRes] = await Promise.allSettled([
          getInspectionPlanByProposal(proposalId),
          AttributeService.getAll(),
          ToolService.getAllTools(),
          listReportFormatAttributeStatuses(),
          listInspectorsAll(),
        ]);
        if (ignore) return;
        if (planRes.status === 'fulfilled') {
          setPlanData(planRes.value ?? null);
        } else {
          setPlanData(null);
        }
        if (attributeRes.status === 'fulfilled') {
          setAttributes(Array.isArray(attributeRes.value?.data) ? attributeRes.value.data : []);
        }
        if (toolRes.status === 'fulfilled') {
          setTools(Array.isArray(toolRes.value?.data) ? toolRes.value.data : []);
        } else {
          setTools([]);
        }
        if (reportFormatStatusRes.status === 'fulfilled') {
          setReportFormatStatuses(Array.isArray(reportFormatStatusRes.value) ? reportFormatStatusRes.value : []);
        } else {
          setReportFormatStatuses([]);
        }
        if (inspectorRes.status === 'fulfilled') {
          setInspectors(Array.isArray(inspectorRes.value) ? inspectorRes.value : []);
        }
      } catch (error) {
        if (!ignore) {
          console.error('InspectorPlanDetail load failed:', error);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, [id, planReloadVersion]);

  const attributeNameById = useMemo(() => {
    const map = new Map<number, string>();
    attributes.forEach((attribute: any) => {
      const attrId = Number(attribute?.id);
      if (!Number.isNaN(attrId)) {
        map.set(attrId, attribute?.name ?? `Parametre #${attrId}`);
      }
    });
    return map;
  }, [attributes]);

  const reportFormatStatusByAttributeId = useMemo(() => {
    const map = new Map<number, ReportFormatStatus>();

    attributes.forEach((attribute: any) => {
      const attributeId = toNumericId(attribute?.id);
      if (attributeId !== undefined) {
        map.set(attributeId, {
          hasReportFormat: false,
          isActive: false,
          isTested: false,
        });
      }
    });

    reportFormatStatuses.forEach((status) => {
      const attributeId = toNumericId(status.attributeId);
      if (attributeId === undefined) {
        return;
      }
      map.set(attributeId, {
        hasReportFormat: status?.hasReportFormat === true,
        isActive: status?.isActive === true,
        isTested: status?.isTested === true,
      });
    });

    return map;
  }, [attributes, reportFormatStatuses]);

  const inspectorNameByAccountId = useMemo(() => {
    const map = new Map<number, string>();
    inspectors.forEach((ins: any) => {
      const accountId = Number(ins?.account?.id ?? ins?.id);
      if (!Number.isNaN(accountId)) {
        const first = ins?.account?.firstName ?? ins?.firstName ?? '';
        const last = ins?.account?.lastName ?? ins?.lastName ?? '';
        const full = `${first} ${last}`.trim();
        map.set(accountId, full.length ? full : `#${accountId}`);
      }
    });
    return map;
  }, [inspectors]);

  const appointments: any[] = Array.isArray(planData?.appointments) ? planData!.appointments : [];
  const tasks: any[] = Array.isArray(planData?.tasks) ? planData!.tasks : [];

  const plannedAccountIds = useMemo(() => {
    const ids = new Set<number>();
    [...appointments, ...tasks].forEach((item: any) => {
      const accountId = resolvePlanAccountId(item);
      if (accountId !== undefined) {
        ids.add(accountId);
      }
    });
    return ids;
  }, [appointments, tasks]);

  const currentInspectorAccountId = useMemo(() => {
    const candidates: unknown[] = [];
    if (typeof window !== 'undefined') {
      candidates.push(localStorage.getItem('inspectorAccountId'));
      candidates.push(localStorage.getItem('accountId'));
      candidates.push(localStorage.getItem('id'));
      candidates.push(localStorage.getItem('inspectorId'));
    }
    if (planData) {
      candidates.push((planData as any)?.accountId);
      candidates.push((planData as any)?.inspectorAccountId);
      candidates.push((planData as any)?.inspectorId);
      candidates.push((planData as any)?.account?.id);
      candidates.push((planData as any)?.inspector?.id);
      candidates.push((planData as any)?.inspector?.accountId);
      candidates.push((planData as any)?.inspector?.account?.id);
    }

    for (const raw of candidates) {
      const numeric = toNumericId(raw);
      if (numeric !== undefined && (!plannedAccountIds.size || plannedAccountIds.has(numeric))) {
        return numeric;
      }
    }
    return undefined;
  }, [planData, plannedAccountIds]);

  const myAppointments = useMemo(() => {
    if (currentInspectorAccountId === undefined) {
      return [];
    }
    return appointments.filter((appt: any) => {
      return resolvePlanAccountId(appt) === currentInspectorAccountId;
    });
  }, [appointments, currentInspectorAccountId]);

  const transportationAppointments = useMemo(() => {
    if (!isInspector) {
      return appointments;
    }
    return myAppointments;
  }, [appointments, isInspector, myAppointments]);

  const allowedTransportationDayKeys = useMemo(() => {
    const dayKeys = new Set<string>();
    transportationAppointments.forEach((appt: any) => {
      const startMoment = dayjs(appt?.startDate ?? appt?.start_date ?? appt?.planningAt);
      if (startMoment.isValid()) {
        dayKeys.add(startMoment.startOf('day').format('YYYY-MM-DD'));
      }
    });
    return dayKeys;
  }, [transportationAppointments]);

  const hasMyPlan = myAppointments.length > 0;

  const isInspectorProcessDone = useMemo(
    () => hasMyPlan && myAppointments.every((appt: any) => appt?.isInspectorProcessDone === true),
    [hasMyPlan, myAppointments],
  );

  const isCompanyProcessDoneForInspector = useMemo(() => {
    if (!hasMyPlan) {
      return false;
    }
    return myAppointments.every((appt: any) => Boolean(appt?.isCompanyProcessDone));
  }, [hasMyPlan, myAppointments]);

  const isContractReadOnly = isCompanyProcessDoneForInspector && isInspectorProcessDone;

  const myContractUrl = useMemo(() => {
    for (const appt of myAppointments) {
      const url = typeof appt?.contractUrl === 'string' ? appt.contractUrl.trim() : '';
      if (url.length) {
        return url;
      }
    }
    return null;
  }, [myAppointments]);

  const myContractId = useMemo(() => {
    for (const appt of myAppointments) {
      const raw = appt?.contractId;
      if (raw != null) {
        const value = String(raw).trim();
        if (value.length) {
          return value;
        }
      }
    }
    return '';
  }, [myAppointments]);

  useEffect(() => {
    setContractIdDraft(myContractId);
  }, [myContractId]);

  useEffect(() => {
    if (!contractDrawerOpen) {
      setContractFileList([]);
    }
  }, [contractDrawerOpen]);


  const proposalIdForControls = useMemo(() => {
    const candidates = [
      proposal?.id,
      (planData as any)?.proposalId,
      (planData as any)?.proposal?.id,
      id,
    ];
    for (const candidate of candidates) {
      const numeric = toNumericId(candidate);
      if (numeric !== undefined) {
        return numeric;
      }
    }
    return undefined;
  }, [proposal?.id, planData, id]);

  const trimmedContractIdDraft = contractIdDraft.trim();
  const canSaveContractId = Boolean(
    hasMyPlan &&
      proposalIdForControls &&
      currentInspectorAccountId &&
      trimmedContractIdDraft.length &&
      contractFileList.length &&
      !contractIdSaving &&
      !isContractReadOnly,
  );

  const handleContractIdSave = async () => {
    if (isContractReadOnly) {
      openNotification('warning', 'Uyarı', 'İşlemler tamamlandığından değişiklik yapamazsınız.');
      return;
    }
    if (!hasMyPlan || !proposalIdForControls || !currentInspectorAccountId) {
      openNotification('error', 'Hata', 'Sözleşme numarası kaydedilemedi.');
      return;
    }

    const trimmed = contractIdDraft.trim();
    if (!trimmed.length) {
      openNotification('warning', 'Uyarı', 'Sözleşme numarası giriniz.');
      return;
    }

    if (!contractFileList.length) {
      openNotification('warning', 'Uyarı', 'Yüklenecek dosyayı seçiniz.');
      return;
    }

    const uploadEntry = contractFileList[0];
    const fileObj: File | Blob | undefined = (uploadEntry as any)?.originFileObj ?? (uploadEntry as any);
    if (!fileObj) {
      openNotification('error', 'Hata', 'Geçersiz dosya seçimi.');
      return;
    }

    try {
      setContractIdSaving(true);
      const uploaded = await DocumentService.postDocument(fileObj, 'appointment_contract');
      const docId = uploaded?.data?.id;
      let url: string | null = typeof uploaded?.data?.preSignUrl === 'string' ? uploaded.data.preSignUrl : null;
      if (!url && typeof uploaded?.data?.preSignedUrl === 'string') {
        url = uploaded.data.preSignedUrl;
      }
      if (!url && typeof uploaded?.data?.url === 'string') {
        url = uploaded.data.url;
      }
      const hasInitialUrl = typeof url === 'string' && url.trim().length > 0;
      if (!hasInitialUrl && docId) {
        const res = await DocumentService.getDocument(docId);
        url = typeof res?.data?.preSignUrl === 'string' ? res.data.preSignUrl : null;
        if (!url && typeof res?.data?.preSignedUrl === 'string') {
          url = res.data.preSignedUrl;
        }
        if (!url && typeof res?.data?.url === 'string') {
          url = res.data.url;
        }
      }
      if (!(typeof url === 'string' && url.trim().length > 0)) {
        openNotification('error', 'Hata', 'Sözleşme yükleme başarısız.');
        return;
      }
      const finalizedUrl = url.trim();

      await bulkUpdateAppointmentsByProposalAndAccount({
        proposalId: proposalIdForControls,
        accountId: currentInspectorAccountId,
        contractId: trimmed,
        contractUrl: finalizedUrl,
        notifyContractUpload: true,
      });
      setPlanData((prev: any) => {
        if (!prev || !Array.isArray((prev as any).appointments)) {
          return prev;
        }
        const updatedAppointments = (prev as any).appointments.map((appt: any) => {
          const accId = toNumericId(
            appt?.accountId ??
              appt?.account?.id ??
              appt?.inspectorId ??
              appt?.inspector?.id ??
              appt?.inspector?.accountId ??
              appt?.inspector?.account?.id,
          );
          if (accId === currentInspectorAccountId) {
            return { ...appt, contractId: trimmed, contractUrl: finalizedUrl };
          }
          return appt;
        });
        return { ...prev, appointments: updatedAppointments };
      });
      setContractIdDraft(trimmed);
      setContractFileList([]);
      openNotification('success', 'Başarılı', 'Sözleşme bilgileri kaydedildi.');
    } catch (error) {
      console.error('InspectorPlanDetail contract save failed:', error);
      openNotification('error', 'Hata', 'Sözleşme bilgileri kaydedilemedi.');
    } finally {
      setContractIdSaving(false);
    }
  };

  const isStatusAllowed = isAdmin || isInspector || (!proposal || proposal.statusId === 5 || proposal.statusId === 7);

  useEffect(() => {
    if (!isStatusAllowed && contractDrawerOpen) {
      setContractDrawerOpen(false);
    }
  }, [isStatusAllowed, contractDrawerOpen]);

  const filteredTasks = useMemo(() => {
    if (!Array.isArray(tasks)) {
      return [];
    }
    if (isAdmin) {
      return tasks;
    }
    if (!isInspector || currentInspectorAccountId === undefined) {
      return tasks;
    }
    return tasks.filter((task: any) => {
      return resolvePlanAccountId(task) === currentInspectorAccountId;
    });
  }, [tasks, currentInspectorAccountId, isAdmin, isInspector]);

  const transportationEntries = useMemo<TransportationEntry[]>(() => {
    if (!Array.isArray(appointments) || !allowedTransportationDayKeys.size) {
      return [];
    }

    return appointments.reduce<TransportationEntry[]>((items, appt: any, idx: number) => {
      const startMoment = dayjs(appt?.startDate ?? appt?.start_date ?? appt?.planningAt);
      if (!startMoment.isValid()) {
        return items;
      }
      const dayKey = startMoment.startOf('day').format('YYYY-MM-DD');
      const appointmentAccountId = resolvePlanAccountId(appt);
      const belongsToInspector = appointmentAccountId === currentInspectorAccountId;
      if (!belongsToInspector && !allowedTransportationDayKeys.has(dayKey)) {
        return items;
      }

      const inspectorId =
        appointmentAccountId ?? currentInspectorAccountId;
      const resolvedInspectorId = inspectorId ?? currentInspectorAccountId ?? idx;
      const inspectorName =
        inspectorNameByAccountId.get(resolvedInspectorId) ?? `#${resolvedInspectorId || idx}`;
      const endMomentCandidate = appt?.endDate ?? appt?.end_date ?? appt?.planningEndDate;
      const endMoment = endMomentCandidate ? dayjs(endMomentCandidate) : null;
      const endValid = endMoment && endMoment.isValid() ? endMoment : startMoment;
      const startTimestamp = startMoment.valueOf();
      const startLocalized = startMoment.locale('tr');
      const endLocalized = endValid.locale('tr');
      const dayLabel = startLocalized.format('DD MMM YYYY');
      const dayOfWeek = startLocalized.format('dddd');
      const timeRange = `${startLocalized.format('HH:mm')} - ${endLocalized.format('HH:mm')}`;
      const fullTimeRange = `${startLocalized.format('DD MMM YYYY HH:mm')} - ${endLocalized.format('DD MMM YYYY HH:mm')}`;
      const transportRaw =
        appt?.transportation ?? appt?.transport ?? appt?.transportType ?? appt?.transportationType;
      const transportValue = typeof transportRaw === 'string' ? transportRaw : undefined;
      const transportLabel = resolveTransportationLabel(transportValue);
      const noteRaw = appt?.note ?? appt?.transportationNote;
      const noteValue = typeof noteRaw === 'string' && noteRaw.trim().length ? noteRaw.trim() : undefined;
      const location = resolveLocationLabel(appt, proposal);
      const latRaw = appt?.latitude ?? appt?.address?.latitude ?? proposal?.address?.latitude;
      const lngRaw = appt?.longitude ?? appt?.address?.longitude ?? proposal?.address?.longitude;
      const lat = typeof latRaw === 'number' && Number.isFinite(latRaw) ? latRaw : undefined;
      const lng = typeof lngRaw === 'number' && Number.isFinite(lngRaw) ? lngRaw : undefined;

      items.push({
        key: `${appt?.id ?? idx}`,
        inspectorName,
        transport: transportValue,
        transportLabel,
        note: noteValue,
        timeRange,
        fullTimeRange,
        dayKey,
        dayLabel,
        dayOfWeek,
        startTimestamp,
        location,
        latitude: lat,
        longitude: lng,
      });
      return items;
    }, []);
  }, [appointments, inspectorNameByAccountId, proposal, currentInspectorAccountId, allowedTransportationDayKeys]);

  const transportLocations = useMemo(
    () => transportationEntries.filter((entry) => entry.latitude != null && entry.longitude != null),
    [transportationEntries],
  );

  const transportationGroups = useMemo<TransportationGroup[]>(() => {
    if (!transportationEntries.length) {
      return [];
    }
    const groupsMap = new Map<string, TransportationGroup>();
    transportationEntries.forEach((entry) => {
      const existing = groupsMap.get(entry.dayKey);
      if (existing) {
        existing.entries.push(entry);
        if (entry.startTimestamp < existing.sortValue) {
          existing.sortValue = entry.startTimestamp;
        }
      } else {
        groupsMap.set(entry.dayKey, {
          dayKey: entry.dayKey,
          dayLabel: entry.dayLabel,
          dayOfWeek: entry.dayOfWeek,
          sortValue: entry.startTimestamp,
          entries: [entry],
        });
      }
    });

    const groups = Array.from(groupsMap.values());
    groups.forEach((group) => {
      group.entries.sort((a, b) => a.startTimestamp - b.startTimestamp);
    });
    groups.sort((a, b) => a.sortValue - b.sortValue);
    return groups;
  }, [transportationEntries]);

  const proposalLatitude = typeof proposal?.address?.latitude === 'number' ? proposal.address.latitude : undefined;
  const proposalLongitude = typeof proposal?.address?.longitude === 'number' ? proposal.address.longitude : undefined;

  const mapCenter = useMemo<[number, number]>(() => {
    if (transportLocations.length) {
      return [transportLocations[0].latitude as number, transportLocations[0].longitude as number];
    }
    if (
      proposalLatitude != null &&
      proposalLongitude != null &&
      Number.isFinite(proposalLatitude) &&
      Number.isFinite(proposalLongitude)
    ) {
      return [proposalLatitude, proposalLongitude];
    }
    return DEFAULT_POSITION;
  }, [transportLocations, proposalLatitude, proposalLongitude]);

  const shouldShowMap =
    transportationEntries.length > 0 &&
    (transportLocations.length > 0 || (proposalLatitude != null && proposalLongitude != null));

  const companyIdForAuthorizedPersons = useMemo(() => {
    const raw = proposal?.company?.id;
    if (raw === null || raw === undefined) {
      return undefined;
    }
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : undefined;
  }, [proposal?.company?.id]);

  const planAuthorizedPersonIds = useMemo(() => {
    const toNumericIds = (value: any): number[] => {
      if (!Array.isArray(value)) {
        return [];
      }
      return value
        .map((entry) => {
          const num = Number(entry);
          return Number.isFinite(num) ? num : null;
        })
        .filter((entry): entry is number => entry !== null && entry !== undefined);
    };
    const planIds = toNumericIds(proposal?.planAuthorizedPersonIds);
    if (planIds.length) {
      return planIds;
    }
    const fallbackIds = toNumericIds(proposal?.authorizedPersonIds);
    return fallbackIds.length ? fallbackIds : undefined;
  }, [proposal?.planAuthorizedPersonIds, proposal?.authorizedPersonIds]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 64 }}>
        <Spin size='large' />
      </div>
    );
  }

 

  const transportationTabContent = (
    <Card className='br-30px' title='Ulaşım'>
      {transportationGroups.length ? (
        <Space direction='vertical' size={16} style={{ width: '100%' }}>
          {transportationGroups.map((group) => (
            <div
              key={group.dayKey}
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 14,
                padding: '16px 18px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Text strong style={{ fontSize: 15 }}>
                  {group.dayLabel}
                </Text>
                <Text type='secondary' style={{ fontSize: 13 }}>
                  {group.dayOfWeek}
                </Text>
              </div>
              <Space direction='vertical' size={12} style={{ width: '100%' }}>
                {group.entries.map((entry) => (
                  <div
                    key={entry.key}
                    style={{
                      border: '1px solid #f5f5f5',
                      borderRadius: 12,
                      padding: '12px 14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <Text strong style={{ fontSize: 14 }}>
                        {entry.inspectorName}
                      </Text>
                      {(entry.transportLabel ?? entry.transport) ? (
                        <Tag color='blue' style={{ marginInlineEnd: 0 }}>
                          {entry.transportLabel ?? entry.transport}
                        </Tag>
                      ) : null}
                    </div>
                    <Text title={entry.fullTimeRange} style={{ fontSize: 13 }}>
                      {entry.timeRange}
                    </Text>
                    <Text style={{ fontSize: 12 }}>
                      Not: {entry.note ?? '-'}
                    </Text>
                  </div>
                ))}
              </Space>
            </div>
          ))}
        </Space>
      ) : (
        <Text>Ulaşım bilgisi bulunamadı.</Text>
      )}
    </Card>
  );

  const pageContent = isStatusAllowed ? (
    <Tabs activeKey={activeTabKey} onChange={handleTabChange}>
      <TabPane tab='Kontrol Adresi' key='control-address'>
        <Space direction='vertical' size={16} style={{ width: '100%' }}>
          <InspectorControlAddressCard
            className='br-30px'
            proposalId={proposal?.id ?? (id ? Number(id) : undefined)}
            proposal={proposal}
            onProposalLoaded={setProposal}
            loading={loading}
            extraActions={
              isCompanyProcessDoneForInspector ? (
                <Button
                  onClick={() => setContractDrawerOpen(true)}
                  disabled={(proposal?.statusId ?? null) !== 7 && !isContractReadOnly}
                >
                  İSG-KATİP İşlemleri
                </Button>
              ) : null
            }
          />
          <IsgKatipContractDrawer
            open={contractDrawerOpen}
            onClose={() => setContractDrawerOpen(false)}
            hasPlan={hasMyPlan}
            contractUrl={myContractUrl}
            contractId={contractIdDraft}
            onContractIdChange={setContractIdDraft}
            onContractIdSave={handleContractIdSave}
            canSaveContractId={canSaveContractId}
            contractFileList={contractFileList}
            onContractFileListChange={setContractFileList}
            saving={contractIdSaving}
            readOnly={isContractReadOnly}
          />
        </Space>
      </TabPane>
      <TabPane tab='Plan Yetkilileri' key='plan-authorized'>
        <Space direction='vertical' size={16} style={{ width: '100%' }}>
          <InspectorPlanAuthorizedContactsCard
            className='br-30px'
            companyId={companyIdForAuthorizedPersons}
            planAuthorizedPersonIds={planAuthorizedPersonIds}
            proposalAuthorizedPersons={
              Array.isArray(proposal?.authorizedPersons) ? proposal?.authorizedPersons : undefined
            }
            loading={loading}
          />
        </Space>
      </TabPane>
      <TabPane tab='Ulaşım' key='transportation'>
        <Space direction='vertical' size={16} style={{ width: '100%' }}>
          {transportationTabContent}
        </Space>
      </TabPane>
      <TabPane tab='Görevler' key='tasks'>
        <Space direction='vertical' size={16} style={{ width: '100%' }}>
          <InspectorPlanTasksCard
            className='br-30px'
            tasks={filteredTasks}
            archiveTasks={tasks}
            attributeNameById={attributeNameById}
            reportFormatStatusByAttributeId={reportFormatStatusByAttributeId}
            proposalId={proposalIdForControls}
            attributes={attributes}
            tools={tools}
            appointments={appointments}
            inspectors={inspectors}
            currentAccountId={currentInspectorAccountId}
            isInspector={isInspector}
            canSelectAllPlanInspectors={isAdmin || isCentralUser}
            onExtraTaskCreated={() => setPlanReloadVersion((prev) => prev + 1)}
          />
        </Space>
      </TabPane>
    </Tabs>
  ) : (
    <Card className='br-30px'>
      <Text>İşlem yapılamaz.</Text>
    </Card>
  );

  return pageContent;
};

export default InspectorPlanDetail;
